import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, BedDouble, Bath, Ruler, Box, Gift, KeyRound } from "lucide-react";
import { asset, money, STATUS } from "@/lib/api";
import { BrandButton } from "@/components/LeadForms";

function usePinDrag(ref, onSelect, onDragEnd) {
  const [drag, setDrag] = useState(null);
  const pct = (e) => {
    const r = ref.current.getBoundingClientRect();
    const c = (v) => Math.min(100, Math.max(0, v));
    return { x: +c(((e.clientX - r.left) / r.width) * 100).toFixed(2), y: +c(((e.clientY - r.top) / r.height) * 100).toFixed(2) };
  };
  if (!onDragEnd) return { drag: null, handlers: () => ({}) };
  const handlers = (p) => ({
    onPointerDown: (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      setDrag({ id: p.id, x: p.x, y: p.y, sx: e.clientX, sy: e.clientY, moved: false });
    },
    onPointerMove: (e) => {
      if (drag?.id !== p.id) return;
      const moved = drag.moved || Math.hypot(e.clientX - drag.sx, e.clientY - drag.sy) > 4;
      setDrag({ ...drag, ...(moved ? pct(e) : {}), moved });
    },
    onPointerUp: async (e) => {
      if (drag?.id !== p.id) return;
      e.stopPropagation();
      if (drag.moved) await onDragEnd(p, { x: drag.x, y: drag.y });
      else onSelect?.(p);
      setDrag(null);
    },
  });
  return { drag, handlers };
}

export function SiteMap({ dev, plots, visible, selectedId, onSelect, onMapClick, placing, onDragEnd }) {
  const ref = useRef(null);
  const { drag, handlers } = usePinDrag(ref, onSelect, onDragEnd);
  return (
    <div
      ref={ref}
      className={`relative overflow-hidden rounded-3xl border border-amber-900/10 bg-[#f4efea] shadow-inner ${placing ? "cursor-crosshair" : ""}`}
      onClick={(e) => {
        if (!onMapClick) return;
        const r = e.currentTarget.getBoundingClientRect();
        onMapClick({ x: +(((e.clientX - r.left) / r.width) * 100).toFixed(2), y: +(((e.clientY - r.top) / r.height) * 100).toFixed(2) });
      }}
      data-testid="site-map"
    >
      {dev.site_plan_url ? <img src={asset(dev.site_plan_url)} alt="Site plan" className="w-full h-auto block select-none" draggable={false} /> : <div className="aspect-[3/2] grid place-items-center text-slate-400">No site plan uploaded</div>}
      {plots.map((p) => {
        const on = !visible || visible.has(p.id);
        const sel = selectedId === p.id;
        const pos = drag?.id === p.id ? drag : p;
        return (
          <button
            key={p.id}
            onClick={(e) => { e.stopPropagation(); if (!onDragEnd) onSelect?.(p); }}
            {...handlers(p)}
            className={`absolute -translate-x-1/2 -translate-y-1/2 grid place-items-center rounded-full text-white font-bold text-xs sm:text-sm border-2 border-white shadow-lg transition-[transform,opacity] duration-200 ${sel ? "scale-125 z-10" : "hover:scale-110"} ${on ? "opacity-100" : "opacity-25"} ${p.status === "available" && on ? "pin-pulse" : ""}`}
            style={{ left: `${pos.x}%`, top: `${pos.y}%`, width: 36, height: 36, background: STATUS[p.status]?.pin, ...(onDragEnd ? { touchAction: "none", cursor: drag?.id === p.id ? "grabbing" : "grab" } : {}) }}
            data-testid={`plot-pin-${p.plot_number}`}
          >
            {p.plot_number}
            {p.incentives?.length > 0 && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-ba border-2 border-white" data-testid={`plot-pin-incentive-${p.plot_number}`} />}
            {p.move_in_ready && <span className="absolute -bottom-1 -left-1 w-4 h-4 rounded-full bg-sky-500 border-2 border-white grid place-items-center" data-testid={`plot-pin-ready-${p.plot_number}`}><KeyRound className="w-2 h-2" /></span>}
          </button>
        );
      })}
    </div>
  );
}

export function PlotCard({ plot, house, onClose, onBook }) {
  const st = STATUS[plot.status];
  const photo = plot.photos?.[0] || house?.photos?.[0];
  return (
    <div className="rounded-3xl bg-white shadow-2xl overflow-hidden rise" data-testid="plot-card">
      <div className="relative">
        {photo && <img src={asset(photo)} alt="" className="w-full aspect-[16/10] object-cover" />}
        <button onClick={onClose} className="absolute top-3 right-3 w-10 h-10 rounded-full glass grid place-items-center" data-testid="plot-card-close"><X className="w-5 h-5" /></button>
        <span className={`absolute top-3 left-3 text-xs uppercase tracking-widest font-semibold px-3 py-1 rounded-full border ${st.cls}`} data-testid="plot-card-status">{st.label}</span>
        {plot.move_in_ready && <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 text-xs uppercase tracking-widest font-semibold px-3 py-1 rounded-full bg-sky-500 text-white" data-testid="plot-card-move-in-ready"><KeyRound className="w-3 h-3" />Move-in ready</span>}
      </div>
      <div className="p-6">
        <p className="text-xs uppercase tracking-widest text-slate-500">Lot {plot.plot_number}</p>
        <h3 className="font-serif text-2xl mt-1">{house?.name || "House"}</h3>
        <div className="flex flex-wrap gap-4 text-sm text-slate-600 mt-3">
          <span className="flex items-center gap-1"><BedDouble className="w-4 h-4" />{plot.beds} beds</span>
          <span className="flex items-center gap-1"><Bath className="w-4 h-4" />{plot.baths} baths</span>
          <span className="flex items-center gap-1"><Ruler className="w-4 h-4" />{plot.sqft} sq ft · {plot.sqm} m²</span>
        </div>
        {plot.incentives?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3" data-testid="plot-card-incentives">
            {plot.incentives.map((t) => <span key={t} className="inline-flex items-center gap-1 text-xs font-semibold rounded-full bg-ba text-white px-3 py-1"><Gift className="w-3 h-3" />{t}</span>)}
          </div>
        )}
        {[["Yard", plot.garden_size], ["Parking", plot.parking], ["Faces", plot.orientation], ["Completion", plot.completion]].some(([, v]) => v) && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-3 text-sm" data-testid="plot-card-extras">
            {[["Yard", plot.garden_size], ["Parking", plot.parking], ["Faces", plot.orientation], ["Completion", plot.completion]].filter(([, v]) => v).map(([k, v]) => (
              <div key={k}><dt className="text-[11px] uppercase tracking-widest text-slate-400">{k}</dt><dd className="text-slate-700">{v}</dd></div>
            ))}
          </dl>
        )}
        {plot.notes && <p className="text-sm text-slate-500 mt-2">{plot.notes}</p>}
        <p className="font-serif text-3xl mt-4 text-bp" data-testid="plot-card-price">{plot.status === "sold" ? "Sold" : money(plot.price)}</p>
        <div className="mt-5 grid gap-2">
          {house && (
            <Link to={`/view/${house.id}?plot=${plot.id}`} data-testid="plot-card-open-tour">
              <BrandButton className="w-full h-12"><Box className="w-4 h-4" />Open 3D tour</BrandButton>
            </Link>
          )}
          {plot.status !== "sold" && <BrandButton variant="ghost" className="w-full h-12" onClick={onBook} data-testid="plot-card-book">Book a viewing</BrandButton>}
        </div>
      </div>
    </div>
  );
}
